<template>
  <span class="sprice" :class="[`sprice--${size}`, { 'is-sale': onSale }]">
    <!-- The struck price is decoration for a sighted reader and noise for a
         screen reader, which would otherwise announce two prices in a row with
         no indication which one is charged. Hide it, and put the whole story
         in one label on the live price. -->
    <span v-if="onSale && showWas" class="sprice__was" aria-hidden="true">{{ format(listPrice) }}</span>
    <span class="sprice__now" :aria-label="ariaLabel">{{ format(price) }}</span>
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  price: { type: [Number, String], default: null },
  /** Undiscounted price. Null when nothing is on sale — the API sends null, not the same number. */
  listPrice: { type: [Number, String], default: null },
  percent: { type: [Number, String], default: null },
  size: { type: String, default: 'md' }, // sm | md | lg
  /**
   * Render the struck-through list price alongside the live one.
   *
   * Off where the price sits in a key too narrow to hold two numbers —
   * a release card's buy button is 74px, and the pair needs 70px BEFORE
   * the format label and the padding, so the two numbers collided and
   * were clipped. Nothing is lost by dropping it there: the card carries
   * a -XX% badge on the artwork, and the aria-label below still
   * announces the discount and the old price in full, so a screen reader
   * hears the same story either way.
   */
  showWas: { type: Boolean, default: true }
})

// Guard on the LIST price being genuinely higher, not just present: a sale
// clamped by the 50-cent floor can leave the two equal, and a strikethrough
// over an identical number reads as a bug.
const onSale = computed(() => {
  const list = Number(props.listPrice)
  const now = Number(props.price)
  return Number.isFinite(list) && Number.isFinite(now) && list > now
})

const format = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? '€' + num.toFixed(2) : '—'
}

const ariaLabel = computed(() => {
  if (!onSale.value) return undefined
  const pct = Number(props.percent)
  const off = Number.isFinite(pct) && pct > 0 ? `, ${pct}% off` : ''
  return `${format(props.price)}${off}, was ${format(props.listPrice)}`
})
</script>

<style scoped>
.sprice {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.sprice__was {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-size: 0.85em;
  color: var(--text-dim);
  text-decoration: line-through;
  /* The tenant accent tints the strike so the two prices read as one
     statement rather than two competing numbers. */
  text-decoration-color: var(--cy-alarm);
  text-decoration-thickness: 1px;
  opacity: 0.7;
  white-space: nowrap;
}

.sprice__now {
  font-family: var(--cy-mono);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: var(--text-main);
  white-space: nowrap;
}

.is-sale .sprice__now {
  color: var(--accent);
}

.sprice--sm { font-size: 0.72rem; }
.sprice--md { font-size: 0.85rem; }
.sprice--lg { font-size: 1rem; }
</style>
